import { useEffect } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { geocodeProgressAtom } from "@/store/geocodeProgress";

/** How long the terminal (done / timed-out) banner lingers before self-clearing. */
const TERMINAL_LINGER_MS = 8000;

/**
 * Global, non-blocking banner shown while imported addresses are still being
 * geocoded in the background (BUG-410).
 *
 * Visually mirrors `BuildRouteProgressBanner`. Renders null when no batch is
 * being tracked — including the case where every imported row already carried
 * coordinates, since the atom is then never seeded (`geocoding_total === 0`).
 *
 * Three states:
 *  - running   — spinner + «Определение адресов: N из M» + progress bar
 *  - completed — every address reached a terminal state (some may have failed)
 *  - timeout   — `pending` stopped moving for the poller's stall window; the
 *    banner names the unresolved count rather than spinning forever. See
 *    `GEOCODE_STALL_TIMEOUT_MS` and BUG-415 for why this state exists.
 */
export function GeocodeProgressBanner() {
  const [task, setTask] = useAtom(geocodeProgressAtom);
  const { t } = useTranslation();

  const outcome = task?.outcome ?? null;

  // Auto-dismiss the terminal banner so it does not stick to the shell forever.
  useEffect(() => {
    if (outcome === null) return;
    const timer = setTimeout(() => setTask(null), TERMINAL_LINGER_MS);
    return () => clearTimeout(timer);
  }, [outcome, setTask]);

  if (!task || task.total <= 0) return null;

  // Before the first poll lands (`pending === null`) nothing is done yet.
  const pending = task.pending ?? task.total;
  const failed = task.failed ?? 0;
  const done = task.done ?? Math.max(0, task.total - pending);
  // A failed address is finished work, not outstanding work — the bar must
  // reach 100% instead of stalling short (matches the backend's progress_pct).
  const settled = Math.max(0, Math.min(task.total, done + failed));
  const pct = task.total > 0 ? Math.round((settled / task.total) * 100) : 100;

  const isTimeout = task.outcome === "timeout";
  const isDone = task.outcome === "completed";
  // The running/completed tone follows the tenant's branding (`brand-primary`,
  // set at runtime by BrandProvider) rather than a hardcoded blue — on a
  // white-label dealer domain a fixed blue reads as someone else's product.
  // The timeout tone stays amber on purpose: there the colour carries the
  // "needs attention" meaning and must not shift with the brand.
  const tone = isTimeout
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : "border-brand-primary/20 bg-brand-primary/5 text-brand-primary";

  let label: string;
  if (isTimeout) {
    label = t("orders.geocodeProgress.stalled", { count: pending });
  } else if (isDone) {
    label = t("orders.geocodeProgress.done", { count: done });
  } else {
    label = t("orders.geocodeProgress.label", {
      done: settled,
      total: task.total,
    });
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="geocode-progress-banner"
      className={`flex items-center gap-3 border-b px-6 py-2 text-sm ${tone}`}
    >
      {task.outcome === null && (
        <svg
          className="h-4 w-4 shrink-0 animate-spin text-brand-primary"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      <span className="font-medium">{label}</span>
      {failed > 0 && (
        <span className="shrink-0 text-amber-700">
          {t("orders.geocodeProgress.failed", { count: failed })}
        </span>
      )}
      <div className="flex-1">
        <div
          className={`h-1.5 w-full overflow-hidden rounded-full ${
            isTimeout ? "bg-amber-200" : "bg-brand-primary/20"
          }`}
        >
          <div
            data-testid="geocode-progress-bar"
            className={`h-full rounded-full transition-all duration-300 ${
              isTimeout ? "bg-amber-500" : "bg-brand-primary"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <span className="shrink-0 tabular-nums">{pct}%</span>
      {task.outcome !== null && (
        <button
          type="button"
          onClick={() => setTask(null)}
          aria-label={t("common.close")}
          className="shrink-0 rounded p-0.5 hover:bg-black/5"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
